import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { USER_ROLE } from "Constants/constant";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import axios from "store/services/axios";
import { authActions } from "store/actions";
import {
  endPlatformGhostSession,
  fetchPlatformSession,
  startPlatformGhostSession,
} from "store/services/platformAdmin.services";
import {
  buildGhostSessionStorage,
  resolveGhostDashboardPath,
} from "pages/PlatformAdmin/Ghost/ghostSession.utils";
import { resolveTenantDeepLinkPath } from "pages/PlatformAdmin/tenantDeepLink.utils";
import { applyPlatformSessionPayload } from "pages/PlatformAdmin/platformSession.utils";
import {
  GHOST_SESSION_KEY,
  PLATFORM_TENANT_CONTEXT_KEY,
  clearGhostSession,
  isGhostSessionActive,
  readGhostSession,
  readPlatformTenantContext,
  writeGhostSession,
  writePlatformTenantContext,
} from "./platformAdminContext.utils";

const SESSION_REFRESH_MIN_INTERVAL_MS = 30_000;

const PlatformAdminContext = createContext(null);

const noopPlatformAdminContext = {
  isPlatformAdmin: false,
  platformUser: null,
  effectiveUser: null,
  tenantContext: null,
  isInTenantContext: false,
  ghostSession: null,
  isGhostActive: false,
  permissionsRevision: 0,
  sessionRefreshing: false,
  enterTenant: () => {},
  exitTenant: () => {},
  startGhost: async () => {},
  endGhost: async () => {},
  refreshPlatformSession: async () => ({ changed: false }),
};

export const usePlatformAdmin = () =>
  useContext(PlatformAdminContext) ?? noopPlatformAdminContext;

export const PlatformAdminProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isPlatformAdmin =
    localStorage.getItem("userType") === USER_ROLE.Super_Admin;

  const [tenantContext, setTenantContextState] = useState(() =>
    readPlatformTenantContext()
  );
  const [ghostSession, setGhostSessionState] = useState(() => readGhostSession());
  const [identityVersion, setIdentityVersion] = useState(0);
  const [permissionsRevision, setPermissionsRevision] = useState(0);
  const [sessionRefreshing, setSessionRefreshing] = useState(false);
  const [ghostSubmitting, setGhostSubmitting] = useState(false);
  const lastSessionRefreshAtRef = useRef(0);
  const sessionRefreshInFlightRef = useRef(null);

  const dispatchPermissionRefresh = useCallback(() => {
    setIdentityVersion((version) => version + 1);
    setPermissionsRevision((version) => version + 1);
    window.dispatchEvent(new Event("refreshPermissions"));
  }, []);

  const refreshPlatformSession = useCallback(
    async ({ silent = true, force = false } = {}) => {
      if (!isPlatformAdmin || isGhostSessionActive()) {
        return { changed: false };
      }

      const now = Date.now();
      if (
        !force &&
        now - lastSessionRefreshAtRef.current < SESSION_REFRESH_MIN_INTERVAL_MS
      ) {
        return { changed: false };
      }

      if (sessionRefreshInFlightRef.current) {
        return sessionRefreshInFlightRef.current;
      }

      const refreshPromise = (async () => {
        setSessionRefreshing(true);

        try {
          const payload = await fetchPlatformSession();
          const { user: nextUser, changed } = applyPlatformSessionPayload(payload);

          if (nextUser) {
            dispatch(authActions.authenticatedUser(nextUser));
          }

          lastSessionRefreshAtRef.current = Date.now();

          if (changed) {
            dispatchPermissionRefresh();
            if (!silent) {
              toast.info("Your platform permissions were updated.");
            }
          }

          return { changed: Boolean(changed) };
        } catch {
          return { changed: false };
        } finally {
          setSessionRefreshing(false);
          sessionRefreshInFlightRef.current = null;
        }
      })();

      sessionRefreshInFlightRef.current = refreshPromise;
      return refreshPromise;
    },
    [dispatch, dispatchPermissionRefresh, isPlatformAdmin]
  );

  const platformUser = useMemo(() => {
    if (!isPlatformAdmin) {
      return null;
    }

    return GetItemFromLocalstorage("user");
  }, [isPlatformAdmin, identityVersion]);

  const effectiveUser = useMemo(() => {
    if (ghostSession?.actingUser) {
      return ghostSession.actingUser;
    }

    return platformUser;
  }, [ghostSession, platformUser]);

  const enterTenant = useCallback(
    ({ spId, spName, path = "/dashboard", returnTo = null }) => {
      const safePath = resolveTenantDeepLinkPath(path);
      if (safePath !== path) {
        toast.warn("That tenant link is not allowed. Opened the tenant dashboard instead.");
      }

      const nextContext = {
        spId,
        spName,
        ...(returnTo ? { returnTo } : {}),
      };
      writePlatformTenantContext(nextContext);
      setTenantContextState(readPlatformTenantContext());
      navigate(safePath);
    },
    [navigate]
  );

  const exitTenant = useCallback(() => {
    const current = readPlatformTenantContext();
    const returnTo = current?.returnTo;
    writePlatformTenantContext(null);
    setTenantContextState(null);
    navigate(returnTo && String(returnTo).startsWith("/") ? returnTo : "/tenants");
  }, [navigate]);

  const endGhost = useCallback(
    async ({ expired = false, silent = false, skipNavigate = false } = {}) => {
      const activeSession = readGhostSession();
      const token = activeSession?.token;

      if (token && !expired) {
        try {
          await endPlatformGhostSession(token);
        } catch (err) {
          if (!silent) {
            toast.error(err?.message || "Failed to end ghost session");
            return;
          }
        }
      }

      clearGhostSession();
      writePlatformTenantContext(null);
      setGhostSessionState(null);
      setTenantContextState(null);
      dispatchPermissionRefresh();

      if (!skipNavigate) {
        navigate("/tenants");
      }

      if (expired) {
        toast.info("Ghost session expired");
      } else if (!silent) {
        toast.success("Ghost session ended");
      }

      await refreshPlatformSession({ silent: true, force: true });
    },
    [navigate, dispatchPermissionRefresh, refreshPlatformSession]
  );

  const startGhost = useCallback(
    async ({
      userId,
      reason,
      spId = null,
      actingUser,
      tenantName = null,
      tenantFlags = null,
      readOnly = false,
    }) => {
      setGhostSubmitting(true);

      try {
        const apiSession = await startPlatformGhostSession({
          user_id: userId,
          reason,
          sp_id: spId,
          read_only: readOnly,
        });

        const storedSession = buildGhostSessionStorage({
          apiSession,
          actingUser,
          tenant: {
            spName: tenantName,
          },
          tenantFlags,
        });

        writeGhostSession(storedSession);
        setGhostSessionState(storedSession);

        if (apiSession.sp_id != null) {
          const nextTenantContext = {
            spId: apiSession.sp_id,
            spName: tenantName || `Tenant ${apiSession.sp_id}`,
          };
          writePlatformTenantContext(nextTenantContext);
          setTenantContextState(nextTenantContext);
          localStorage.setItem(
            "spSettings",
            JSON.stringify({
              id: apiSession.sp_id,
              name: nextTenantContext.spName,
            })
          );
        }

        setIdentityVersion((version) => version + 1);
        dispatchPermissionRefresh();

        const dashboardPath = resolveGhostDashboardPath(actingUser.user_type);
        navigate(dashboardPath);
        toast.success("Ghost session started");
      } finally {
        setGhostSubmitting(false);
      }
    },
    [navigate, dispatchPermissionRefresh]
  );

  useEffect(() => {
    if (!isPlatformAdmin || isGhostSessionActive()) {
      return undefined;
    }

    refreshPlatformSession({ silent: true, force: true });
  }, [isPlatformAdmin, refreshPlatformSession]);

  useEffect(() => {
    if (!isPlatformAdmin || isGhostSessionActive()) {
      return undefined;
    }

    const onFocus = () => {
      refreshPlatformSession({ silent: true });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshPlatformSession({ silent: true });
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isPlatformAdmin, refreshPlatformSession]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === PLATFORM_TENANT_CONTEXT_KEY) {
        setTenantContextState(readPlatformTenantContext());
      }

      if (event.key === GHOST_SESSION_KEY) {
        setGhostSessionState(readGhostSession());
      }
    };

    const onIdentityRefresh = () => {
      setIdentityVersion((version) => version + 1);
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("refreshPermissions", onIdentityRefresh);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("refreshPermissions", onIdentityRefresh);
    };
  }, []);

  useEffect(() => {
    if (!isPlatformAdmin) {
      return undefined;
    }

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const activeGhost = readGhostSession();
        if (!activeGhost?.token) {
          return Promise.reject(error);
        }

        const status = error?.response?.status;
        const message = String(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            ""
        ).toLowerCase();

        // "Ghost session is read-only..." rejections of mutations must not end
        // the session — only genuinely invalid/expired ghost tokens should.
        if (
          (status === 403 || status === 401) &&
          message.includes("ghost session") &&
          !message.includes("read-only")
        ) {
          endGhost({ expired: true, silent: true });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [endGhost, isPlatformAdmin]);

  const value = useMemo(
    () => ({
      isPlatformAdmin,
      platformUser,
      effectiveUser,
      tenantContext,
      isInTenantContext: Boolean(tenantContext?.spId),
      ghostSession,
      isGhostActive: Boolean(ghostSession?.token),
      ghostSubmitting,
      permissionsRevision,
      sessionRefreshing,
      enterTenant,
      exitTenant,
      startGhost,
      endGhost,
      refreshPlatformSession,
    }),
    [
      isPlatformAdmin,
      platformUser,
      effectiveUser,
      tenantContext,
      ghostSession,
      ghostSubmitting,
      permissionsRevision,
      sessionRefreshing,
      enterTenant,
      exitTenant,
      startGhost,
      endGhost,
      refreshPlatformSession,
    ]
  );

  if (!isPlatformAdmin) {
    return children;
  }

  return (
    <PlatformAdminContext.Provider value={value}>
      {children}
    </PlatformAdminContext.Provider>
  );
};
